import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { Loader2, ClipboardList, Phone, Mail, ChevronRight, ExternalLink, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const STATUS_COLORS = {
  new: 'text-blue-400 bg-blue-900/20 border-blue-800/50',
  contacted: 'text-[#3B82F6] bg-[#3B82F6]/10 border-[#3B82F6]/30',
  qualified: 'text-teal-400 bg-teal-900/20 border-teal-800/50',
  declined: 'text-red-400 bg-red-900/20 border-red-800/50',
  converted: 'text-[#57EB31] bg-[#57EB31]/10 border-[#57EB31]/30',
};

export default function Applications() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [showSendInvite, setShowSendInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', company_name: '', contact_name: '', personal_message: '' });
  const [sendingInvite, setSendingInvite] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { load(); }, [filter]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/applications', { params: { status: filter || undefined } });
      setApps(r.data);
    } catch { toast.error('Failed to load applications'); }
    finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/applications/${id}`, { status });
    toast.success(`Status updated to ${status}`);
    load();
    setSelected(null);
  };

  const convertToCustomer = async (app) => {
    navigate(`/ops/customers?prefill=${encodeURIComponent(JSON.stringify({ company_name: app.company_name, contact_email: app.email, contact_phone: app.phone }))}`);
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    setSendingInvite(true);
    try {
      const r = await api.post('/applications/send-invite', inviteForm);
      toast.success(`Invite sent to ${inviteForm.email}!`);
      setShowSendInvite(false);
      setInviteForm({ email: '', company_name: '', contact_name: '', personal_message: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send invite'); }
    finally { setSendingInvite(false); }
  };

  return (
    <div className="p-6 space-y-5 animate-fade-in" data-testid="applications-page">
      <div className="flex items-center justify-between pb-4 border-b border-[#2D3F5E]/60">
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-1">// SALES PIPELINE</p>
          <h1 className="text-white font-['Manrope'] font-extrabold text-2xl">Applications</h1>
          <p className="text-[#B8C0CC] font-['IBM_Plex_Sans'] text-sm mt-0.5">{apps.length} applications</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowSendInvite(true)} data-testid="send-invite-btn"
            className="flex items-center gap-2 px-4 py-2 border border-[#2D3F5E] text-[#B8C0CC] rounded-sm text-sm hover:border-[#3B82F6]/50 hover:text-[#3B82F6] transition-colors font-['IBM_Plex_Sans']">
            <Mail size={14} /> Send Invite
          </button>
          <a href="/apply" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-sm font-['Manrope'] font-bold text-sm uppercase tracking-wide hover:bg-[#2563EB] transition-colors">
            View Public Form →
          </a>
        </div>
      </div>

      <select value={filter} onChange={e => setFilter(e.target.value)}
        className="h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]">
        <option value="">All Applications</option>
        {['new','contacted','qualified','declined','converted'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
      </select>

      <div className="card-industrial overflow-hidden">
        {loading ? <div className="flex justify-center py-12"><Loader2 className="text-[#3B82F6] animate-spin" size={24} /></div> : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#2D3F5E]">
                {['COMPANY','CONTACT','FLEET SIZE','STATE','PAIN POINTS','STATUS',''].map(h => (
                  <th key={h} className={`text-left px-4 py-3 text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {apps.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-16">
                  <ClipboardList size={28} className="mx-auto mb-3 text-[#2D3F5E]" />
                  <p className="text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm">No applications yet. Share the public form link or generate a private link.</p>
                </td></tr>
              ) : apps.map((a, i) => (
                <tr key={a.id} className="border-b border-[#2D3F5E]/30 hover:bg-[#2A1C14] cursor-pointer transition-colors"
                  style={{ background: i % 2 !== 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}
                  onClick={() => setSelected(a)} data-testid={`app-row-${a.id}`}>
                  <td className="px-4 py-3 text-sm text-white font-['Manrope'] font-bold">{a.company_name}</td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-[#E8EAED] font-['IBM_Plex_Sans']">{a.contact_name}</p>
                    <p className="text-xs text-[#5C6A7A] font-['JetBrains_Mono']">{a.phone}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#B8C0CC] font-['IBM_Plex_Sans']">{a.fleet_size_range || '—'}</td>
                  <td className="px-4 py-3 text-xs font-['JetBrains_Mono'] text-[#B8C0CC]">{a.us_state || '—'}</td>
                  <td className="px-4 py-3 text-xs text-[#5C6A7A] font-['IBM_Plex_Sans'] max-w-[180px] truncate">{a.pain_points?.join(', ') || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] ${STATUS_COLORS[a.status] || STATUS_COLORS.new}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#5C6A7A] text-xs">{a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Application detail modal */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="bg-[#1A2332] border-[#2D3F5E] max-w-lg">
          <DialogHeader><DialogTitle className="text-white font-['Manrope']">{selected?.company_name}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                {[['Contact',selected.contact_name],['Email',selected.email],['Phone',selected.phone],['State',selected.us_state||'—'],['Fleet Size',selected.fleet_size_range||'—'],['Maintenance',selected.current_maintenance||'—']].map(([l,v]) => (
                  <div key={l} className="bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm p-3">
                    <p className="text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">{l}</p>
                    <p className="text-sm text-white font-['IBM_Plex_Sans']">{v}</p>
                  </div>
                ))}
              </div>
              {selected.pain_points?.length > 0 && (
                <div className="bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm p-3">
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-2">Pain Points</p>
                  {selected.pain_points.map(p => <p key={p} className="text-sm text-[#B8C0CC] font-['IBM_Plex_Sans'] mb-1">• {p}</p>)}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {['contacted','qualified','declined'].map(s => (
                  <button key={s} onClick={() => updateStatus(selected.id, s)}
                    className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wide border transition-colors font-['JetBrains_Mono'] ${STATUS_COLORS[s]}`}>
                    → {s}
                  </button>
                ))}
                <button onClick={() => convertToCustomer(selected)}
                  className="px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wide bg-[#3B82F6] text-white hover:bg-[#2563EB] transition-colors font-['JetBrains_Mono']">
                  Convert to Customer →
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Invite Modal */}
      <Dialog open={showSendInvite} onOpenChange={v => { setShowSendInvite(v); }}>
        <DialogContent className="bg-[#1A2332] border-[#2D3F5E]">
          <DialogHeader><DialogTitle className="text-white font-['Manrope']">Send Application Invite</DialogTitle></DialogHeader>
          <form onSubmit={sendInvite} className="space-y-4 mt-2">
            <p className="text-[#B8C0CC] font-['IBM_Plex_Sans'] text-sm">The prospect will receive an email with a personalized apply link. Submission goes directly to your sales queue.</p>
            {[['Email *','email','email',true],['Company Name','company_name','text',false],['Contact Name','contact_name','text',false]].map(([l,k,t,r]) => (
              <div key={k}>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">{l}</label>
                <input type={t} required={r} value={inviteForm[k]} onChange={e => setInviteForm(p => ({...p,[k]:e.target.value}))}
                  className="w-full h-10 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]"
                  data-testid={`invite-${k}`} />
              </div>
            ))}
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Personal Message (optional)</label>
              <textarea value={inviteForm.personal_message} onChange={e => setInviteForm(p => ({...p, personal_message: e.target.value}))} rows={2}
                className="w-full px-3 py-2 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6] resize-none"
                placeholder="Hi, we spoke earlier about your fleet..." />
            </div>
            <button type="submit" disabled={sendingInvite} data-testid="send-invite-submit"
              className="w-full h-10 bg-[#3B82F6] text-white font-['Manrope'] font-bold text-sm uppercase tracking-wide rounded-sm hover:bg-[#2563EB] disabled:opacity-50 flex items-center justify-center gap-2">
              {sendingInvite && <Loader2 size={14} className="animate-spin" />} <Send size={14} /> Send Invite Email
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
